import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';
import { TimerProvider } from './contexts/TimerContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { CallProvider } from './contexts/CallContext';
import { SpotifyProvider } from './contexts/SpotifyContext';
import IncomingCallModal from './components/chat/IncomingCallModal';
import CallScreen from './components/chat/CallScreen';

// Import route configuration
import { generateRoutes } from './routes';

// Import components
import AuthForm from './components/AuthForm';
import Layout from './components/Layout';

// Configure axios
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.baseURL = API_BASE_URL;

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  // Set up axios interceptors for global error handling
  useEffect(() => {
    // Add response interceptor for rate limiting
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle rate limit errors globally - but only if not already handled by specific functions
        if (error.response?.status === 429) {
          // Check if this error is from login/register/2fa endpoints - let them handle it
          const url = error.config?.url || '';
          if (url.includes('/api/users/login') || 
              url.includes('/api/users/register') || 
              url.includes('/api/users/complete-2fa-login')) {
            // Let the specific function handle this error
            return Promise.reject(error);
          }
          
          // Handle rate limit errors for other endpoints
          const errorData = error.response?.data;
          if (errorData && typeof errorData === 'object') {
            const message = errorData.message || errorData.detail || 'Too many requests. Please try again later.';
            
            // Just show the backend message - it already includes the time
            toast.error(message);
          } else {
            toast.error('Too many requests. Please try again later.');
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TimerProvider>
            <Router>
              <WebSocketProvider>
                <SpotifyProvider>
                  <CallProvider>
                    <div className="App">
                      <Routes>
                        {generateRoutes()}
                      </Routes>
                      <IncomingCallModal />
                      <CallScreen />
                      <Toaster position="top-right" />
                    </div>
                  </CallProvider>
                </SpotifyProvider>
              </WebSocketProvider>
            </Router>
          </TimerProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;